import { useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { HelperText } from '@pulse-web-ui/helper-text';
import { ArrowRight } from '@pulse-web-ui/icons';
import { useTheme } from '@pulse-web-ui/theme';
import { observer } from 'mobx-react-lite';

import { useStores } from '@shared/hooks';
import { addTestAttribute } from '@shared/utils';

import type { OtpHandleResendError, OtpType } from '../../model';
import { Resend } from '../resend';
import {
  Button,
  ButtonContainer,
  CodeContainer,
  CodeInputWrapper,
  OTPContainer,
  OTPInput,
} from './embedded-otp.styles';

const OTP_MAX_LENGTH = 5;
export interface EmbeddedOtpProps {
  otpType: OtpType;
  code: string;
  phone: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  onResendError?: OtpHandleResendError;
  onResend?: () => void;
  isFetching?: boolean;
  error?: string;
  helperTextError?: string;
}

export const EmbeddedOtp = observer(
  ({
    otpType,
    code,
    phone,
    onCodeChange,
    onSubmit,
    onResend,
    onResendError,
    isFetching,
    error,
    helperTextError,
  }: EmbeddedOtpProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const {
      MainStore: {
        agentUserStore: { setConfirmToken },
      },
    } = useStores();
    const otpCodeRef = useRef<HTMLInputElement | null>(null);

    return (
      <CodeContainer>
        <CodeInputWrapper>
          <HelperText
            status={error || helperTextError ? 'error' : 'default'}
            message={<pre>{error || helperTextError}</pre>}
            testId='otp-code-input.error'
          >
            <OTPContainer>
              <OTPInput
                name='code'
                placeholder='00000'
                value={code}
                inputMode='numeric'
                label={t('OTP:labels.smsCode')}
                maxLength={OTP_MAX_LENGTH}
                ref={(e) => (otpCodeRef.current = e)}
                onChange={onCodeChange}
                error={!!error}
                {...addTestAttribute('otp-code-input.input')}
              />

              <ButtonContainer>
                <Button
                  size='s'
                  icon={
                    <ArrowRight
                      width={30}
                      color={theme.colors.icon.inverse}
                    />
                  }
                  onClick={onSubmit}
                  disabled={!!error || !code}
                  $error={!!error}
                  isLoading={isFetching}
                  {...addTestAttribute('otp-button-send')}
                />
              </ButtonContainer>
            </OTPContainer>
          </HelperText>
          <Resend
            otpType={otpType}
            onSuccess={setConfirmToken}
            onError={onResendError}
            onResend={onResend}
            phone={phone}
          />
        </CodeInputWrapper>
      </CodeContainer>
    );
  }
);
